// ClientTransactions.js
import React from "react";
import { formatApiDateTime, parseApiDateTime } from "../../../lib/helpers";

function ClientTransactions({ transactions, clientId, bare = false }) {
  console.log('🔍 [ClientTransactions] Component rendered with:', {
    transactionsCount: transactions?.length,
    clientId,
    transactionsType: typeof transactions,
    isArray: Array.isArray(transactions)
  });

  const validTransactions = Array.isArray(transactions) ? transactions.filter((t) => t.status !== 'reversed') : [];
  const filtered = clientId
    ? validTransactions.filter((t) => t.user_id === clientId)
    : [];
  const sorted = [...filtered].sort(
    (a, b) => parseApiDateTime(b.timestamp) - parseApiDateTime(a.timestamp)
  );

  console.log('🔍 [ClientTransactions] After filtering:', {
    validCount: validTransactions.length,
    filteredCount: filtered.length,
    sortedCount: sorted.length
  });

  // Debug: verificar estructura de transacciones
  React.useEffect(() => {
    console.log('🔍 [ClientTransactions] useEffect triggered, sorted.length:', sorted.length);
    if (sorted.length > 0) {
      console.log('🔍 [ClientTransactions] Total transacciones filtradas:', sorted.length);

      // Buscar transacciones de tipo purchase
      const purchaseTransactions = sorted.filter(tx =>
        String(tx.type || '').toLowerCase() === 'purchase'
      );
      console.log('🔍 [ClientTransactions] Transacciones de tipo purchase:', purchaseTransactions.length);

      if (purchaseTransactions.length > 0) {
        console.log('🔍 [ClientTransactions] Primera transacción PURCHASE:', purchaseTransactions[0]);
        console.log('🔍 [ClientTransactions] Items de primera PURCHASE:', purchaseTransactions[0]?.items);
        console.log('🔍 [ClientTransactions] Items es array?', Array.isArray(purchaseTransactions[0]?.items));
        console.log('🔍 [ClientTransactions] Items length:', purchaseTransactions[0]?.items?.length || 0);
      } else {
        console.log('🔍 [ClientTransactions] ⚠️ No hay transacciones de tipo purchase');
      }

      // Mostrar todas las transacciones y sus tipos
      const types = sorted.map(tx => ({ type: tx.type, hasItems: tx.items?.length > 0, itemsCount: tx.items?.length || 0 }));
      console.log('🔍 [ClientTransactions] Tipos de transacciones:', types);
    } else {
      console.log('🔍 [ClientTransactions] ⚠️ No hay transacciones filtradas');
    }
  }, [sorted]);

  // Función para traducir tipos de transacción
  const translateTransactionType = (type) => {
    const typeStr = String(type || '').toLowerCase();
    const translations = {
      'sale': 'Venta',
      'purchase': 'Venta',
      'add_cash': 'Añadir Crédito',
      'refund': 'Reembolso',
      'reverse_add_cash': 'Reversar Crédito'
    };
    return translations[typeStr] || type || 'Desconocido';
  };

  if (!clientId) {
    return <p className="text-center text-gray-500">No hay cliente seleccionado.</p>;
  }

  return (
    <div className={bare ? "overflow-x-auto" : "bg-white p-4 md:p-6 rounded-lg shadow border border-gray-200 overflow-x-auto"}>
      {!bare && (
        <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4">
          Historial de Movimientos
        </h2>
      )}
      <table className="min-w-full table-auto text-gray-700 text-xs md:text-sm">
        <thead className="bg-gray-50 text-gray-700 uppercase text-[10px] md:text-xs">
          <tr>
            <th className="py-2 px-2 sm:px-4 text-left">Fecha</th>
            <th className="py-2 px-2 sm:px-4 text-left">Tipo</th>
            <th className="py-2 px-2 sm:px-4 text-left">Productos</th>
            <th className="py-2 px-2 sm:px-4 text-right">Cantidad</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((tx) => (
            <tr
              key={tx.id}
              className="border-b odd:bg-white even:bg-gray-50 hover:bg-gray-100"
            >
              <td className="py-2 px-2 sm:px-4 whitespace-nowrap">
                {formatApiDateTime(tx.timestamp)}
              </td>
              <td className="py-2 px-2 sm:px-4">{translateTransactionType(tx.type)}</td>

              <td className="py-2 px-2 sm:px-4">
                {(() => {
                  // Debug detallado
                  console.log('🔍 Transaction ID:', tx.id, 'Type:', tx.type);
                  console.log('🔍 Items raw:', tx.items);
                  console.log('🔍 Items type:', typeof tx.items, 'Is array?', Array.isArray(tx.items));

                  // Verificar si hay items - múltiples formas de verificar
                  const items = tx.items || tx.Items || [];
                  const hasItems = Array.isArray(items) && items.length > 0;

                  console.log('🔍 Has items?', hasItems, 'Items length:', items.length);

                  if (hasItems) {
                    return (
                      <div className="space-y-1">
                        {items.map((item, i) => {
                          console.log('🔍 Item', i, ':', item);
                          const qty = parseFloat(item.quantity || item.Quantity || 0);
                          const ut = item.unit_type || item.unitType;
                          // Peso: 'g' si el producto es por peso; 'u' si es por unidad.
                          // (unit_type viene del backend; si falta, cae al heurístico de entero.)
                          const isWeight = ut ? ut === 'weight' : (qty % 1 !== 0);
                          const unitLabel = isWeight ? 'g' : 'u';
                          const trim = (n) => parseFloat(Number(n).toFixed(3)).toString();
                          const productName = item.product_name || item.productName || item.ProductName || 'Producto desconocido';
                          const realW = item.real_weight ?? item.realWeight;
                          const realWNum = realW != null ? parseFloat(realW) : null;

                          return (
                            <div key={i} className="text-xs">
                              <span className="font-medium">{productName}</span>
                              <span className="text-gray-600 ml-1">Peso {trim(qty)}{unitLabel}</span>
                              {isWeight && realWNum != null && realWNum > 0 && (
                                <span className="text-gray-500 ml-1">· báscula {trim(realWNum)}g</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  } else {
                    // Mostrar información de debug si no hay items
                    return (
                      <span className="text-gray-400 italic text-xs">
                        {tx.type === 'purchase' || tx.type === 'PURCHASE' ? 'Sin productos' : '-'}
                      </span>
                    );
                  }
                })()}
              </td>

              <td className={`py-2 px-2 sm:px-4 text-right font-semibold ${String(tx.type || '').toLowerCase() === 'add_cash' ? 'text-green-600' :
                  String(tx.type || '').toLowerCase() === 'refund' || String(tx.type || '').toLowerCase() === 'reverse_add_cash' ? 'text-red-600' :
                    'text-gray-800'
                }`}>
                {String(tx.type || '').toLowerCase() === 'add_cash' ? '+' :
                  String(tx.type || '').toLowerCase() === 'refund' || String(tx.type || '').toLowerCase() === 'reverse_add_cash' ? '-' : ''}
                €{Math.abs(tx.amount || 0).toFixed(2)}
              </td>
            </tr>
          ))}
          {sorted.length === 0 && (
            <tr>
              <td
                colSpan={4}
                className="py-4 text-center text-gray-500 italic"
              >
                No se encontraron transacciones.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default ClientTransactions;
